import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiInfo, FiAlertTriangle, FiActivity } = FiIcons;

const FaultLegend = () => {
  const faultTypes = [
    {
      type: 'Major Fault',
      color: '#dc2626',
      weight: 3,
      description: 'Primary fault systems with high seismic risk',
      examples: ['Philippine Fault Zone', 'Marikina Valley Fault'],
      riskLevel: 'critical'
    },
    {
      type: 'Strike-Slip Fault',
      color: '#ea580c',
      weight: 2,
      description: 'Faults where blocks move horizontally past each other',
      examples: ['West Valley Fault', 'East Valley Fault'],
      riskLevel: 'high'
    },
    {
      type: 'Subduction Zone',
      color: '#2563eb',
      weight: 3,
      dashArray: '10, 5',
      description: 'Where one tectonic plate dives beneath another',
      examples: ['Philippine Trench', 'Manila Trench'],
      riskLevel: 'critical'
    }
  ];

  const riskLevels = {
    critical: {
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      icon: FiAlertTriangle,
      label: 'Critical Risk'
    },
    high: {
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      icon: FiAlertTriangle,
      label: 'High Risk'
    }
  };

  const getRiskConfig = (level) => riskLevels[level] || riskLevels.high;

  const FaultLineVisualization = ({ fault }) => (
    <div className="w-16 h-5 bg-gray-100 rounded flex items-center justify-center relative">
      <div
        className={`absolute ${fault.dashArray ? 'border-dashed' : ''}`}
        style={{
          backgroundColor: fault.color,
          height: `${fault.weight}px`,
          width: '50px',
          borderTopStyle: fault.dashArray ? 'dashed' : 'solid',
          borderTopWidth: fault.dashArray ? '2px' : '0',
          borderColor: fault.color
        }}
      />
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md p-4 border border-gray-200"
    >
      <div className="flex items-center space-x-2 mb-4">
        <SafeIcon icon={FiActivity} className="text-blue-600" />
        <h3 className="font-semibold text-gray-900">Fault Line Information</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {faultTypes.map((fault, index) => (
          <motion.div
            key={fault.type}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gray-50 rounded-lg p-3 border border-gray-200"
          >
            <div className="flex items-center space-x-2 mb-2">
              <FaultLineVisualization fault={fault} />
              <div className="flex-1">
                <div className="font-medium text-sm text-gray-900">
                  {fault.type}
                </div>
              </div>
            </div>

            <div className="text-xs text-gray-600 mb-2">
              {fault.description}
            </div>

            <div className="space-y-1 mb-2">
              <div className="text-xs text-gray-500 font-medium">Examples:</div>
              {fault.examples.map((example, idx) => (
                <div key={idx} className="text-xs text-gray-600 ml-2">
                  • {example}
                </div>
              ))}
            </div>

            <div>
              <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getRiskConfig(fault.riskLevel).bgColor} ${getRiskConfig(fault.riskLevel).color} ${getRiskConfig(fault.riskLevel).borderColor} border`}>
                <SafeIcon icon={getRiskConfig(fault.riskLevel).icon} className="text-xs" />
                <span>{getRiskConfig(fault.riskLevel).label}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Risk Information</h4>
            <div className="space-y-2 text-xs text-gray-600">
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-red-600 rounded-full mt-0.5 flex-shrink-0"></div>
                <span>
                  <strong>Critical:</strong> Capable of generating M7.0+ earthquakes with widespread damage
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-0.5 flex-shrink-0"></div>
                <span>
                  <strong>High:</strong> Can produce significant earthquakes with localized damage
                </span>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <SafeIcon icon={FiInfo} className="text-blue-600 text-xs mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-800">
                  <strong>Note:</strong> Fault lines represent geological features. Earthquake locations may not always align perfectly with mapped faults due to complex subsurface geology.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FaultLegend;